import { RootStoreSession } from "../root-store";
import { Compensation } from "../compensation/compensation-store";
import { ActivityLogEntry } from "../activity-log-entry/activity-log-entry-store";
import { computed, makeObservable, observable } from "mobx";
import { Nullable, Optional } from "../../globals/types";
import { Constants } from "../../globals/constants";
import { CreationProjectDto, UsageProjectDto } from "./project-dto";
import { CreationArea, UsageArea } from "../area/area";
import moment from "moment";
import { Contact } from "../contact/contact-store";
import { DataNotLoaded } from "../../globals/errors";
import { User } from "../user/user-store";
import _ from "lodash";
import { Municipality } from "../municipality/municipality-store";
import { desc, label } from "../../reflection/reflection";

export type ActionStatus = "actionRequired" | "noActionRequired" | "completed";

export class ProjectFactory {
    static makeProject(projectDto: CreationProjectDto | UsageProjectDto, rootStore: RootStoreSession): Project {
        switch (projectDto.type) {
            case Constants.projectTypeConstructionPermit:
                return new ConstructionPermitProject(projectDto as UsageProjectDto, rootStore);
            case Constants.projectTypeLandUsePlanChange:
                return new LandUsePlanChangeProject(projectDto as UsageProjectDto, rootStore);
            case Constants.projectTypeLandfillSite:
                return new LandfillSiteProject(projectDto as UsageProjectDto, rootStore);
            case Constants.projectTypeSoilImprovement:
                return new SoilImprovementProject(projectDto as CreationProjectDto, rootStore);
            case Constants.projectTypeDezoning:
                return new DezoningProject(projectDto as CreationProjectDto, rootStore);
            case Constants.projectTypeRecultivation:
                return new RecultivationProject(projectDto as CreationProjectDto, rootStore);
            default:
                throw new Error("Unbekanntes Projekt");
        }
    }
}

export interface ICmiDocumentationRawi {
    get cmiNumberRawi(): Optional<string>;
}

export interface ICmiDocumentationRawiUwe {
    get cmiDocumentsRawiPre(): string[];

    get cmiDocumentsRawiApproved(): string[];

    get cmiDocumentsUwePre(): string[];

    get cmiDocumentsUweApproved(): string[];
}

export abstract class Project {
    protected owner_?: User;
    protected issues_?: string[];
    rootStore: RootStoreSession;

    protected constructor(rootStore: RootStoreSession) {
        this.rootStore = rootStore;

        makeObservable(this, {
            id: computed,
            name: computed,
            municipality: computed,
            municipalityName: computed,
            place: computed,
            category: computed,
            type: computed,
            fullType: computed,
            ebageNr: computed,
            owner: computed,
            updatedAtTimestamp: computed,
            updatedAt: computed,
            updatedBy: computed,
            status: computed,
            enactmentDateDisplayText: computed,
            enactmentDate: computed,
            totalArea: computed,
            totalAreaNek2: computed,
            totalAreaNek3: computed,
            totalAreaNek4: computed,
            totalAreaNek5: computed,
            totalAreaNek6: computed,
            totalAreaNekUnknown: computed,
            totalEffectiveAreaNek2: computed,
            totalEffectiveAreaNek3: computed,
            totalEffectiveAreaNek4: computed,
            totalEffectiveAreaNek5: computed,
            totalEffectiveAreaNek6: computed,
            totalEffectiveAreaNekUnknown: computed,
            compensations: computed,
            issues: computed,
            actionStatus: computed,
            geometrySource: computed,
            geometrySourceDate: computed
        });
    }

    abstract get id(): number;

    abstract get name(): string;

    abstract get municipality(): Optional<Municipality>;

    abstract get municipalityName(): string;

    abstract get place(): Optional<string>;

    abstract get category(): string;

    abstract get categoryKey(): string;

    abstract get fullType(): string;

    abstract get type(): string;

    abstract get typeKey(): string;

    abstract get ebageNr(): Optional<string>;

    abstract get owner(): User;

    abstract get status(): string;

    abstract get enactmentDateDisplayText(): string;

    abstract get enactmentDate(): Nullable<Date>;

    abstract get enactmentDateTimestamp(): number;

    abstract get totalArea(): number;

    abstract get totalAreaNek2(): number;

    abstract get totalAreaNek3(): number;

    abstract get totalAreaNek4(): number;

    abstract get totalAreaNek5(): number;

    abstract get totalAreaNek6(): number;

    abstract get totalAreaNekUnknown(): number;

    abstract get totalEffectiveAreaNek2(): number;

    abstract get totalEffectiveAreaNek3(): number;

    abstract get totalEffectiveAreaNek4(): number;

    abstract get totalEffectiveAreaNek5(): number;

    abstract get totalEffectiveAreaNek6(): number;

    abstract get totalEffectiveAreaNekUnknown(): number;

    abstract get updatedBy(): User;

    abstract get updatedAtTimestamp(): number;

    abstract get updatedAt(): string;

    abstract get compensations(): Compensation[];

    abstract get actionStatus(): ActionStatus;

    abstract get issues(): string[];

    abstract get geometrySource(): Optional<string>;

    abstract get geometrySourceDate(): Nullable<Date>;

    abstract get geometrySourceDateDisplayText(): string;
}

export abstract class CreationProject extends Project {
    private updatedBy_?: User;
    private updatedAtTimestamp_?: number;
    private areas_?: CreationArea[];
    private compensations_?: Compensation[];
    projectDto: CreationProjectDto;

    protected constructor(projectDto: CreationProjectDto, rootStore: RootStoreSession) {
        super(rootStore);
        this.projectDto = projectDto;

        makeObservable(this, {
            projectDto: observable,
            approvedCreationArea: computed,
            areas: computed,
            generalRemarks: computed,
            totalAreaClaimed: computed,
            totalAreaAvailable: computed,
            pngMin: computed,
            pngMax: computed
        });
    }

    @label("ID")
    get id(): number {
        return this.projectDto.id;
    }

    @label("Name")
    get name(): string {
        return this.projectDto.name;
    }

    get municipality(): Optional<Municipality> {
        if (this.projectDto.municipality !== null) {
            return this.rootStore.municipalityStore.getMunicipality(this.projectDto.municipality);
        }

        return undefined;
    }

    @label("Gemeinde")
    @desc("Name der Gemeinde. Bei Projekten über mehrere Gemeinden wird nur die Gemeinde mit dem grössten Anteil der Hauptnutzung erfasst.")
    get municipalityName(): string {
        return this.municipality?.name ?? "";
    }

    @label("Orts- oder Flurname")
    @desc("Ortschaft oder Flurname, darf leer bleiben bei OP-Gesamtrevision.")
    get place(): Optional<string> {
        return this.projectDto.place ?? undefined;
    }

    @label("Baugesuchsnummer")
    @desc("Baugesuchsnummer im Falle eines Bauprojektes. Bei Nutzungsplanänderung bleibt das Feld leer. Bei bauprojektbezogenen Einzonungen ist es optional.")
    get ebageNr(): Optional<string> {
        return this.projectDto.ebageNr ?? undefined;
    }

    get actionStatus(): ActionStatus {
        return this.issues.length > 0 ? "actionRequired" : "noActionRequired";
    }

    @label("Handlungsbedarf")
    get actionStatusDisplayText(): string {
        return this.rootStore.keyValueStore.getActionStatusDisplayText(this.actionStatus);
    }

    get issues(): string[] {
        if (this.issues_ === undefined) {
            const issues = [];

            if (this.areas.length === 0) {
                issues.push("Keine Neuschaffungsfläche");
            }

            const criticalStates = ["", "zurückgezogen", "sistiert", "abgelehnt", "abgebrochen", "abnahme_mit_maengel"]
            if (this.areas.filter(a => criticalStates.includes(a.areaDto.creationStatus ?? "")).length !== 0) {
                issues.push("Flächen mit besonderem Schaffungsstatus");
            }

            if (this.totalAreaClaimed > this.totalEffectiveAreaCreated) {
                issues.push("Totale Fläche überbeansprucht");
            }

            this.issues_ = issues;
        }

        return this.issues_;
    }

    get categoryKey(): string {
        return this.projectDto.category;
    }

    get category(): string {
        return this.rootStore.keyValueStore.getProjectCategoryDisplayText(this.projectDto.category);
    }

    @label("Art des Vorhabens")
    get fullType(): string {
        return this.rootStore.keyValueStore.getFullProjectTypeDisplayText(this.projectDto.type);
    }

    get type(): string {
        return this.rootStore.keyValueStore.getProjectTypeDisplayText(this.projectDto.type);
    }

    get typeKey(): string {
        return this.projectDto.type;
    }

    get owner(): User {
        if (this.owner_ === undefined) {
            const user = this.rootStore.userStore.users.find(u => u.id == this.projectDto.ownerId);

            if (user !== undefined) {
                this.owner_ = user;
            } else {
                throw new DataNotLoaded("User not loaded");
            }

        }

        return this.owner_;
    }

    @label("Verfahrensstatus")
    get status(): string {
        return this.rootStore.keyValueStore.getProjectStatusDisplayText(this.projectDto.status);
    }

    @label("Bewilligtes Neuschaffungsziel [m²]")
    @desc("Insgesamt neu zu schaffende FFF in m² gemäss Bewilligung.")
    get approvedCreationArea(): Optional<number> {
        return this.projectDto.approvedCreationArea ?? undefined;
    }

    @label("Inkraftsetzungsdatum")
    @desc("Datum des letztinstanzlichen Entscheids (Genehmigungsdatum durch den Regierungsrat bei Nutzungsplanänderungen bzw. Beschlussdatum bei Baubewilligungen). Dies ist gleichbedeutend mit dem Datum der Inkraftsetzung. Im Falle eines Deponie- oder Abbauprojektes wird das Datum des RRE erfasst.")
    get enactmentDateDisplayText(): string {
        return this.projectDto.enactmentDate !== null
            ? moment(this.projectDto.enactmentDate).format(Constants.dateFormat)
            : "";
    }

    get enactmentDateTimestamp(): number {
        return this.projectDto.enactmentDate !== null
            ? moment(this.projectDto.enactmentDate).unix()
            : -1;
    }

    get enactmentDate(): Nullable<Date> {
        return this.projectDto.enactmentDate !== null
            ? moment(this.projectDto.enactmentDate).toDate()
            : null;
    }

    @label("Bemerkungen")
    get generalRemarks(): Optional<string> {
        return this.projectDto.generalRemarks ?? undefined;
    }

    get totalArea(): number {
        return this.areas.reduce((result, area) => {
            return result + area.areaRounded
        }, 0);
    }

    @label("Totale geschaffene Fläche")
    get totalEffectiveAreaCreated(): number {
        return this.areas.reduce((result, area) => {
            return result + area.effectiveAreaRounded
        }, 0);
    }

    get totalAreaNek2(): number {
        return this.areas.filter(a => a.nek === 2).reduce((result, area) => {
            return result + area.areaRounded
        }, 0);
    }

    get totalAreaNek3(): number {
        return this.areas.filter(a => a.nek === 3).reduce((result, area) => {
            return result + area.areaRounded
        }, 0);
    }

    get totalAreaNek4(): number {
        return this.areas.filter(a => a.nek === 4).reduce((result, area) => {
            return result + area.areaRounded
        }, 0);
    }

    get totalAreaNek5(): number {
        return this.areas.filter(a => a.nek === 5).reduce((result, area) => {
            return result + area.areaRounded
        }, 0);
    }

    get totalAreaNek6(): number {
        return this.areas.filter(a => a.nek === 6).reduce((result, area) => {
            return result + area.areaRounded
        }, 0);
    }

    get totalAreaNekUnknown(): number {
        return this.areas.filter(a => a.nek === 99).reduce((result, area) => {
            return result + area.areaRounded
        }, 0);
    }

    get totalEffectiveAreaNek2(): number {
        return this.areas.filter(a => a.nek === 2).reduce((result, area) => {
            return result + area.effectiveAreaRounded
        }, 0);
    }

    get totalEffectiveAreaNek3(): number {
        return this.areas.filter(a => a.nek === 3).reduce((result, area) => {
            return result + area.effectiveAreaRounded
        }, 0);
    }

    get totalEffectiveAreaNek4(): number {
        return this.areas.filter(a => a.nek === 4).reduce((result, area) => {
            return result + area.effectiveAreaRounded
        }, 0);
    }

    get totalEffectiveAreaNek5(): number {
        return this.areas.filter(a => a.nek === 5).reduce((result, area) => {
            return result + area.effectiveAreaRounded
        }, 0);
    }

    get totalEffectiveAreaNek6(): number {
        return this.areas.filter(a => a.nek === 6).reduce((result, area) => {
            return result + area.effectiveAreaRounded
        }, 0);
    }

    get totalEffectiveAreaNekUnknown(): number {
        return this.areas.filter(a => a.nek === 99).reduce((result, area) => {
            return result + area.effectiveAreaRounded
        }, 0);
    }

    get totalAreaClaimed(): number {
        return this.compensations.reduce((sum, compensation) => {
            return sum + (compensation.area ?? 0);
        }, 0);
    }

    @label("Verfügbare Fläche")
    get totalAreaAvailable(): number {
        return this.totalEffectiveAreaCreated - this.totalAreaClaimed;
    }

    get pngMin(): number {
        const pngs = this.areas.filter(a => a.png !== undefined).map(a => a.png) as number[];
        return pngs.length > 0 ? Math.min(...pngs) : 0;
    }

    get pngMax(): number {
        const pngs = this.areas.filter(a => a.png !== undefined).map(a => a.png) as number[];
        return pngs.length > 0 ? Math.max(...pngs) : 0;
    }

    get areas(): CreationArea[] {
        if (this.areas_ === undefined) {
            this.areas_ = this.rootStore.areaStore.creationAreas.filter(a => a.projectId === this.id);
        }

        return this.areas_;
    }

    get updatedBy(): User {
        if (this.updatedBy_ === undefined) {
            const updateEntries = this.rootStore.activityLogEntryStore.activityLogEntries[Constants.creationProjectTableName]
                .filter(e => e.rowId === this.id && e.action === "update");
            const sortedUpdateEntries: ActivityLogEntry[] = _.sortBy(updateEntries, ["timestamp"]);
            const userId = _.last<ActivityLogEntry>(sortedUpdateEntries)?.userId;
            const user = this.rootStore.userStore.users.find(u => u.id === userId);

            if (user !== undefined) {
                this.updatedBy_ = user;
            } else {
                throw new DataNotLoaded("User not loaded");
            }
        }

        return this.updatedBy_;
    }

    get updatedAtTimestamp(): number {
        if (this.updatedAtTimestamp_ === undefined) {
            const updateEntries = this.rootStore.activityLogEntryStore.activityLogEntries[Constants.creationProjectTableName]
                .filter(e => e.rowId === this.id && e.action === "update");
            const sortedUpdateEntries: ActivityLogEntry[] = _.sortBy(updateEntries, ["timestamp"]);
            const timestamp = _.last<ActivityLogEntry>(sortedUpdateEntries)?.timestamp;

            if (timestamp !== undefined) {
                this.updatedAtTimestamp_ = timestamp;
            } else {
                throw new Error();
            }
        }

        return this.updatedAtTimestamp_!;
    }

    @label("Letzte Änderung")
    get updatedAt(): string {
        return moment.unix(this.updatedAtTimestamp).format(Constants.timestampFormat);
    }

    get compensations(): Compensation[] {
        if (this.compensations_ === undefined) {
            this.compensations_ = this.rootStore.compensationStore.compensations.filter(c => c.creationProjectId === this.id);
        }

        return this.compensations_;
    }

    @label("Quelle Geometrie")
    @desc("Name des Planes oder Dokuments, aus welchem die aktuelle Geometrie im Informationssystem erstellt wurde. Ist bei Plananpassungen durch die KS FFF zu aktualisieren.")
    get geometrySource(): Optional<string> {
        return this.projectDto.geometrySource ?? undefined;
    }

    get geometrySourceDate(): Nullable<Date> {
        return this.projectDto.geometrySourceDate !== null
            ? moment(this.projectDto.geometrySourceDate).toDate()
            : null;
    }

    @label("Datum Quelle Geometrie")
    @desc("Datum des Planes oder Dokuments, aus welchem die aktuelle Geometrie im Informationssystem erstellt wurde. Ist bei Plananpassungen durch die KS FFF zu aktualisieren.")
    get geometrySourceDateDisplayText(): string {
        return this.projectDto.geometrySourceDate !== null
            ? moment(this.projectDto.geometrySourceDate).format(Constants.dateFormat)
            : "";
    }
}

export class SoilImprovementProject extends CreationProject implements ICmiDocumentationRawi {
    private landOwner_?: Contact;
    private constructionSupervisor_?: Contact;

    constructor(creationProjectDto: CreationProjectDto, rootStore: RootStoreSession) {
        super(creationProjectDto, rootStore);
        makeObservable(this, {
            landOwner: computed,
            landOwnerFullName: computed,
            constructionSupervisor: computed,
            constructionSupervisorFullName: computed,
            creationGoalSource: computed,
            enactmentRemarks: computed,
            cmiNumberRawi: computed,
            creationGoalRemarks: computed,
            requirementsSpecificationBBBStatus: computed,
            requirementsSpecificationRequestSource: computed,
            startConstructionMeetingStatus: computed,
            startConstructionMeetingSource: computed,
        });
    }

    @label("Quelle Neuschaffungsziel")
    get creationGoalSource(): string {
        return this.rootStore.keyValueStore.getCreationGoalSourceDisplayText(this.projectDto.creationGoalSource ?? undefined);
    }

    @label("Bemerkungen Inkraftsetzung")
    get enactmentRemarks(): Optional<string> {
        return this.projectDto.enactmentRemarks ?? undefined;
    }

    @label("CMI-Nummer rawi")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle rawi. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiNumberRawi(): Optional<string> {
        return this.projectDto.cmiDocuments.find(d => d.department === Constants.departmentRawi && d.type === null)?.number;
    }

    @label("Stand Pflichtenheft BBB")
    @desc("Auflagenkontrolle: Ist im Rahmen eines Beizugs einer bodenkundlichen Baubegleitung ein Pflichtenheft einzureichen, respektive ist dieses vorhanden?")
    get requirementsSpecificationBBBStatus(): string {
        return this.rootStore.keyValueStore.getConditionControlStatusDisplayText(this.projectDto.requirementsSpecificationBBBStatus ?? undefined);
    }

    @label("Quelle Pflichtenheftforderung")
    @desc("Auflagenkontrolle: In welchem Dokument wurde ein Pflichtenheft im Rahmen einer bodenkundlichen Baubegleitung gefordert?")
    get requirementsSpecificationRequestSource(): string {
        return this.rootStore.keyValueStore.getConditionControlSourceDisplayText(this.projectDto.requirementsSpecificationRequestSource ?? undefined);
    }

    @label("Stand Startbausitzung")
    @desc("Auflagenkontrolle: Wurde eine Startbausitzung durchgeführt?")
    get startConstructionMeetingStatus(): string {
        return this.rootStore.keyValueStore.getConditionControlStatusDisplayText(this.projectDto.startConstructionMeetingStatus ?? undefined);
    }

    @label("Quelle Startbausitzung")
    @desc("Auflagenkontrolle: In welchem Dokument wurde eine Startbausitzung gefordert?")
    get startConstructionMeetingSource(): string {
        return this.rootStore.keyValueStore.getConditionControlSourceDisplayText(this.projectDto.startConstructionMeetingSource ?? undefined);
    }

    @label("Bemerkungen Aufwertungsziel")
    get creationGoalRemarks(): Optional<string> {
        return this.projectDto.creationGoalRemarks ?? undefined;
    }

    get landOwner(): Optional<Contact> {
        if (this.landOwner_ === undefined) {
            const contactDto = this.projectDto.contacts.find(c => c.type === Constants.contactTypeOwner);

            if (contactDto !== undefined) {
                const contact = this.rootStore.contactStore.contacts.find(c => c.id === contactDto.id);

                if (contact !== undefined) {
                    this.landOwner_ = contact;
                } else {
                    throw new DataNotLoaded("Contact not loaded");
                }
            }
        }

        return this.landOwner_;
    }

    @label("Kompensationseigner")
    @desc("Kontaktadresse der Kompensationseigner*innen. Muss nur erfasst werden, wenn die Kompensationseigner*innen vom Regelfall (Grundeigentümer*innen bei Rückzonung und Bauherr*innen bei Aufwertungsprojekten) abweichen. Wird eine Teilfläche zu Kompensationszwecken abgetreten, wird der Eigner in der Regel nicht aktualisiert.")
    get landOwnerFullName(): Optional<string> {
        return this.landOwner?.fullName ?? "";
    }

    get constructionSupervisor(): Optional<Contact> {
        if (this.constructionSupervisor_ === undefined) {
            const contactDto = this.projectDto.contacts.find(c => c.type === Constants.contactTypeConstructionSupervisor);

            if (contactDto !== undefined) {
                const contact = this.rootStore.contactStore.contacts.find(c => c.id === contactDto.id);

                if (contact !== undefined) {
                    this.constructionSupervisor_ = contact;
                } else {
                    throw new DataNotLoaded("Contact not loaded");
                }
            }
        }

        return this.constructionSupervisor_;
    }

    @label("Bodenkundliche Baubegleitung")
    @desc("Kontaktadresse der Bodenkundlichen Baubegleitung.")
    get constructionSupervisorFullName(): Optional<string> {
        return this.constructionSupervisor?.fullName ?? "";
    }
}

export class DezoningProject extends CreationProject implements ICmiDocumentationRawiUwe {
    private landOwner_?: Contact;

    constructor(creationProjectDto: CreationProjectDto, rootStore: RootStoreSession) {
        super(creationProjectDto, rootStore);
        makeObservable(this, {
            landOwner: computed,
            landOwnerFullName: computed,
            revision: computed,
            governmentCouncilDecisionNr: computed,
            cmiDocumentsRawiPre: computed,
            cmiDocumentsRawiApproved: computed,
            cmiDocumentsUwePre: computed,
            cmiDocumentsUweApproved: computed,
        });
    }

    @label("Revisionsart")
    @desc("Nutzungsplanänderungen werden zwischen Gesamtrevision und Teilrevision unterschieden.")
    get revision(): string {
        return this.rootStore.keyValueStore.getRevisionTypeDisplayText(this.projectDto.revision ?? undefined);
    }

    @label("RRE-Nummer")
    @desc("Nummer des Regierungsratsentscheids. Ist zwingend bei der Verfahrensstatusänderung anzugeben im Falle einer Nutzungsplanänderung. Bauprojekte, die vom Regierungsrat beschlossen werden, haben auch eine RRE-Nummer, welche anzugeben ist. Bei anderen Baugesuchen bleibt das Feld leer. ")
    get governmentCouncilDecisionNr(): Optional<string> {
        return this.projectDto.governmentCouncilDecisionNr ?? undefined;
    }

    @label("CMI-Nummer rawi (Vorprüfung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle rawi. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsRawiPre(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentRawi && d.type === "vorprüfung").map(d => d.number);
    }

    @label("CMI-Nummer rawi (Genehmigung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle rawi. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsRawiApproved(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentRawi && d.type === "genehmigung").map(d => d.number);
    }

    @label("CMI-Nummer uwe (Vorprüfung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle uwe. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsUwePre(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentUwe && d.type === "vorprüfung").map(d => d.number);
    }

    @label("CMI-Nummer uwe (Genehmigung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle uwe. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsUweApproved(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentUwe && d.type === "genehmigung").map(d => d.number);
    }

    get landOwner(): Optional<Contact> {
        if (this.landOwner_ === undefined) {
            const contactDto = this.projectDto.contacts.find(c => c.type === Constants.contactTypeOwner);

            if (contactDto !== undefined) {
                const contact = this.rootStore.contactStore.contacts.find(c => c.id === contactDto.id);

                if (contact !== undefined) {
                    this.landOwner_ = contact;
                } else {
                    throw new DataNotLoaded("Contact not loaded");
                }
            }
        }

        return this.landOwner_;
    }

    @label("Kompensationseigner")
    @desc("Kontaktadresse der Kompensationseigner*innen. Muss nur erfasst werden, wenn die Kompensationseigner*innen vom Regelfall (Grundeigentümer*innen bei Rückzonung und Bauherr*innen bei Aufwertungsprojekten) abweichen. Wird eine Teilfläche zu Kompensationszwecken abgetreten, wird der Eigner in der Regel nicht aktualisiert.")
    get landOwnerFullName(): Optional<string> {
        return this.landOwner?.fullName ?? "";
    }
}

export class RecultivationProject extends CreationProject {
    private constructionSupervisor_?: Contact;

    constructor(creationProjectDto: CreationProjectDto, rootStore: RootStoreSession) {
        super(creationProjectDto, rootStore);
        makeObservable(this, {
            approvedRecultivationArea: computed,
            operatingPermitDate: computed,
            operatingPermitDateDisplayText: computed,
            maintenanceDirectiveDate: computed,
            maintenanceDirectiveDateDisplayText: computed,
            creationGoalSource: computed,
            requirementsSpecificationBBBStatus: computed,
            requirementsSpecificationRequestSource: computed,
            startConstructionMeetingStatus: computed,
            startConstructionMeetingSource: computed,
            constructionSupervisor: computed,
            constructionSupervisorFullName: computed,
            enactmentRemarks: computed,
            creationGoalRemarks: computed
        });
    }

    @label("Verfügbare Fläche")
    get totalAreaAvailable(): number {
        return 0;
    }

    @label("Bewilligtes Rekultivierungsziel [m²]")
    @desc("Insgesamt zu rekultivierende FFF in m² gemäss Bewilligung.")
    get approvedCreationArea(): Optional<number> {
        return this.projectDto.approvedCreationArea ?? undefined;
    }

    @label("Bewilligte Rekultivierungsfläche [m²]")
    @desc("Diese umfasst nicht nur FFF und kann somit grösser sein, als das Rekultivierungsziel (z.B. gehören auch ökologische Ausgleichsmassnahmen dazu, welche nicht zur FFF gerechnet werden). Wird durch die DS uwe verwaltet.")
    get approvedRecultivationArea(): Optional<number> {
        return this.projectDto.approvedRecultivationArea ?? undefined;
    }

    get operatingPermitDate(): Nullable<Date> {
        return this.projectDto.operatingPermitDate !== null
            ? moment(this.projectDto.operatingPermitDate).toDate()
            : null;
    }

    @label("Betriebsbewilligung")
    @desc("Datum der letzten Betriebsbewilligung im Falle eines Abbau- oder Deponieprojektes.")
    get operatingPermitDateDisplayText(): string {
        return this.projectDto.operatingPermitDate !== null
            ? moment(this.projectDto.operatingPermitDate).format(Constants.dateFormat)
            : "";
    }

    get maintenanceDirectiveDate(): Nullable<Date> {
        return this.projectDto.maintenanceDirectiveDate !== null
            ? moment(this.projectDto.maintenanceDirectiveDate).toDate()
            : null;
    }

    @label("Nachsorgeverfügung")
    @desc("Datum der Nachsorgeverfügung im Falle eines Abbau- oder Deponieprojektes.")
    get maintenanceDirectiveDateDisplayText(): string {
        return this.projectDto.maintenanceDirectiveDate !== null
            ? moment(this.projectDto.maintenanceDirectiveDate).format(Constants.dateFormat)
            : "";
    }

    @label("Quelle Rekultivierungsziels")
    get creationGoalSource(): string {
        return this.rootStore.keyValueStore.getCreationGoalSourceDisplayText(this.projectDto.creationGoalSource ?? undefined);
    }

    @label("Stand Pflichtenheft BBB")
    @desc("Auflagenkontrolle: Ist im Rahmen eines Beizugs einer bodenkundlichen Baubegleitung ein Pflichtenheft einzureichen, respektive ist dieses vorhanden?")
    get requirementsSpecificationBBBStatus(): string {
        return this.rootStore.keyValueStore.getConditionControlStatusDisplayText(this.projectDto.requirementsSpecificationBBBStatus ?? undefined);
    }

    @label("Quelle Pflichtenheftforderung")
    @desc("Auflagenkontrolle: In welchem Dokument wurde ein Pflichtenheft im Rahmen einer bodenkundlichen Baubegleitung gefordert?")
    get requirementsSpecificationRequestSource(): string {
        return this.rootStore.keyValueStore.getConditionControlSourceDisplayText(this.projectDto.requirementsSpecificationRequestSource ?? undefined);
    }

    @label("Stand Startbausitzung")
    @desc("Auflagenkontrolle: Wurde eine Startbausitzung durchgeführt?")
    get startConstructionMeetingStatus(): string {
        return this.rootStore.keyValueStore.getConditionControlStatusDisplayText(this.projectDto.startConstructionMeetingStatus ?? undefined);
    }

    @label("Quelle Startbausitzung")
    @desc("Auflagenkontrolle: In welchem Dokument wurde eine Startbausitzung gefordert?")
    get startConstructionMeetingSource(): string {
        return this.rootStore.keyValueStore.getConditionControlSourceDisplayText(this.projectDto.startConstructionMeetingSource ?? undefined);
    }

    get constructionSupervisor(): Optional<Contact> {
        if (this.constructionSupervisor_ === undefined) {
            const contactDto = this.projectDto.contacts.find(c => c.type === Constants.contactTypeConstructionSupervisor);

            if (contactDto !== undefined) {
                const contact = this.rootStore.contactStore.contacts.find(c => c.id === contactDto.id);

                if (contact !== undefined) {
                    this.constructionSupervisor_ = contact;
                } else {
                    throw new DataNotLoaded("Contact not loaded");
                }
            }
        }

        return this.constructionSupervisor_;
    }

    @label("Bodenkundliche Baubegleitung")
    get constructionSupervisorFullName(): Optional<string> {
        return this.constructionSupervisor?.fullName ?? "";
    }

    @label("Bemerkungen Inkraftsetzung")
    get enactmentRemarks(): Optional<string> {
        return this.projectDto.enactmentRemarks ?? undefined;
    }

    @label("Bemerkungen Aufwertungsziel")
    get creationGoalRemarks(): Optional<string> {
        return this.projectDto.creationGoalRemarks ?? undefined;
    }
}

export abstract class UsageProject extends Project {
    private updatedBy_?: User;
    private updatedAtTimestamp_?: number;
    private areas_?: UsageArea[];
    private compensations_?: Compensation[];
    private realizationDelayed_?: boolean;
    projectDto: UsageProjectDto;

    protected constructor(projectDto: UsageProjectDto, rootStore: RootStoreSession) {
        super(rootStore);
        this.projectDto = projectDto;
        makeObservable(this, {
            projectDto: observable,
            areas: computed,
            governmentCouncilDecisionNr: computed,
            compensationDeadlineDisplayText: computed,
            compensationDeadline: computed,
            remarks: computed,
            compensationApproved: computed,
            totalAreaCompensated: computed,
            totalAreaToCompensate: computed,
            compensationBalance: computed,
            realizationDelayed: computed
        });
    }

    @label("ID")
    get id(): number {
        return this.projectDto.id;
    }

    @label("Name")
    get name(): string {
        return this.projectDto.name;
    }

    get municipality(): Optional<Municipality> {
        if (this.projectDto.municipality !== null) {
            return this.rootStore.municipalityStore.getMunicipality(this.projectDto.municipality);
        }

        return undefined;
    }

    @label("Gemeinde")
    @desc("Name der Gemeinde. Bei Projekten über mehrere Gemeinden wird nur die Gemeinde mit dem grössten Anteil der Hauptnutzung erfasst.")
    get municipalityName(): string {
        return this.municipality?.name ?? "";
    }

    @label("Orts- oder Flurname")
    @desc("Ortschaft oder Flurname, darf leer bleiben bei OP-Gesamtrevision.")
    get place(): Optional<string> {
        return this.projectDto.place ?? undefined;
    }

    get categoryKey(): string {
        return this.projectDto.category;
    }

    get category(): string {
        return this.rootStore.keyValueStore.getProjectCategoryDisplayText(this.projectDto.category);
    }

    @label("Art des Vorhabens")
    get fullType(): string {
        return this.rootStore.keyValueStore.getFullProjectTypeDisplayText(this.projectDto.type);
    }

    get type(): string {
        return this.rootStore.keyValueStore.getProjectTypeDisplayText(this.projectDto.type);
    }

    get typeKey(): string {
        return this.projectDto.type;
    }

    @label("Verfahrensstatus")
    get status(): string {
        return this.rootStore.keyValueStore.getProjectStatusDisplayText(this.projectDto.status);
    }

    @label("RRE-Nummer")
    @desc("Nummer des Regierungsratsentscheids. Ist zwingend bei der Verfahrensstatusänderung anzugeben im Falle einer Nutzungsplanänderung. Bauprojekte, die vom Regierungsrat beschlossen werden, haben auch eine RRE-Nummer, welche anzugeben ist. Bei anderen Baugesuchen bleibt das Feld leer.")
    get governmentCouncilDecisionNr(): Optional<string> {
        return this.projectDto.governmentCouncilDecisionNr ?? undefined;
    }

    get owner(): User {
        if (this.owner_ === undefined) {
            const user = this.rootStore.userStore.users.find(u => u.id == this.projectDto.ownerId);

            if (user !== undefined) {
                this.owner_ = user;
            } else {
                throw new DataNotLoaded("User not loaded");
            }
        }

        return this.owner_;
    }

    @label("Inkraftsetzungsdatum")
    @desc("Datum des letztinstanzlichen Entscheids (Genehmigungsdatum durch den Regierungsrat bei Nutzungsplanänderungen bzw. Beschlussdatum bei Baubewilligungen). Dies ist gleichbedeutend mit dem Datum der Inkraftsetzung. Im Falle eines Deponie- oder Abbauprojektes wird das Datum des RRE erfasst.")
    get enactmentDateDisplayText(): string {
        return this.projectDto.enactmentDate !== null
            ? moment(this.projectDto.enactmentDate).format(Constants.dateFormat)
            : "";
    }

    get enactmentDateTimestamp(): number {
        return this.projectDto.enactmentDate !== null
            ? moment(this.projectDto.enactmentDate).unix()
            : -1;
    }

    get enactmentDate(): Nullable<Date> {
        return this.projectDto.enactmentDate !== null
            ? moment(this.projectDto.enactmentDate).toDate()
            : null;
    }

    get actionStatus(): ActionStatus {
        if (this.compensationApproved) {
            return "completed";
        }

        if (this.projectDto.status === "sistiert") {
            return "completed";
        }

        const hasIssues = this.issues.length > 0;

        if (hasIssues) {
            return "actionRequired";
        }

        const allCompensationsCompleted = this.compensations.every(c => c.completed);
        if (this.projectDto.status === "in_kraft" && allCompensationsCompleted) {
            return "completed";
        }

        return "noActionRequired";
    }

    @label("Handlungsbedarf")
    get actionStatusDisplayText(): string {
        return this.rootStore.keyValueStore.getActionStatusDisplayText(this.actionStatus);
    }

    @label("Verzug Umsetzung")
    get realizationDelayed(): boolean {
        if (this.realizationDelayed_ === undefined) {
            const criticalStates = ["", "in_bearbeitung", "zurueckgezogen", "abgelehnt", "bewilligt"];

            if (this.enactmentDate === null) {
                this.realizationDelayed_ = false;
                return this.realizationDelayed_;
            }

            const now = moment();
            const delay = now.diff(this.enactmentDate, "years");

            if (delay < 2) {
                this.realizationDelayed_ = false;
                return this.realizationDelayed_;
            }

            const creationProjects = this.compensations.map(c => c.creationProject);

            for (const p of creationProjects) {
                for (const a of p.areas) {
                    if (criticalStates.includes(a.creationStatus)) {
                        this.realizationDelayed_ = true;
                        return this.realizationDelayed_;
                    }
                }
            }

            this.realizationDelayed_ = false;
        }

        return this.realizationDelayed_;
    }

    get issues(): string[] {
        if (this.issues_ === undefined) {
            const issues = [];

            const noArea = this.areas.length === 0;
            const noCompensation = this.compensations.length === 0;
            const compensationCritical = this.compensations.some(c => c.creationProject.actionStatus === "actionRequired");
            const compensationFactor = this.totalAreaToCompensate / this.totalEffectiveAreaUsed;
            const underCompensated = compensationFactor > Constants.underCompensationThreshold;
            const overUsedCompensation = this.compensations.some(c => c.creationProject.totalAreaAvailable < 0);

            if (noArea) {
                issues.push("Keine Beanspruchungsfläche");
            }

            if (noCompensation) {
                issues.push("Keine Kompensation");
            }

            if (compensationCritical) {
                issues.push("Handlungsbedarf bei Neuschaffungsprojekt");
            }

            if (overUsedCompensation) {
                issues.push("Überbeanspruchte Kompensation");
            }

            if (underCompensated) {
                issues.push("Unterkompensation");
            }

            this.issues_ = issues;
        }

        return this.issues_;
    }

    @label("Kompensationsdatum")
    @desc("Datum bis zu welchem die Kompensation zu erfolgen hat.")
    get compensationDeadlineDisplayText(): string {
        return this.projectDto.compensationDeadline !== null
            ? moment(this.projectDto.compensationDeadline).format(Constants.dateFormat)
            : "";
    }

    get compensationDeadline(): Nullable<Date> {
        return this.projectDto.compensationDeadline !== null
            ? moment(this.projectDto.compensationDeadline).toDate()
            : null;
    }

    @label("Baugesuchsnummer")
    @desc("Baugesuchsnummer im Falle eines Bauprojektes, bei Nutzungsplanänderung bleibt das Feld leer. Bei bauprojektbezogenen Einzonungen ist sie optional. ")
    get ebageNr(): Optional<string> {
        return this.projectDto.ebageNr ?? undefined;
    }

    @label("Bemerkungen")
    get remarks(): Optional<string> {
        return this.projectDto.remarks ?? undefined;
    }

    @label("Kompensation genehmigen")
    @desc("Schliesst ein Projekt ab und übersteuert den Status Kompensationspflicht. So können auch nicht korrekt kompensierte Projekte abgeschlossen werden (Z. B. wenn die Flächenabweichung nur geringfügig ist.)")
    get compensationApproved(): boolean {
        return this.projectDto.compensationApproved;
    }

    get totalArea(): number {
        return this.areas.reduce((result, area) => {
            return result + area.areaRounded
        }, 0);
    }

    get totalAreaNek2(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEK2 ?? 0)
        }, 0);
    }

    get totalAreaNek3(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEK3 ?? 0)
        }, 0);
    }

    get totalAreaNek4(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEK4 ?? 0)
        }, 0);
    }

    get totalAreaNek5(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEK5 ?? 0)
        }, 0);
    }

    get totalAreaNek6(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEK6 ?? 0)
        }, 0);
    }

    get totalAreaNekUnknown(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEKUnknown ?? 0)
        }, 0);
    }

    @label("Totale beanspruchte Fläche")
    get totalEffectiveAreaUsed(): number {
        return this.areas.reduce((result, area) => {
            return result + area.effectiveAreaRounded
        }, 0);
    }

    get totalEffectiveAreaNek2(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEK2 ?? 0)
        }, 0);
    }

    get totalEffectiveAreaNek3(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEK3 ?? 0)
        }, 0);
    }

    get totalEffectiveAreaNek4(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEK4 ?? 0)
        }, 0);
    }

    get totalEffectiveAreaNek5(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEK5 ?? 0)
        }, 0);
    }

    get totalEffectiveAreaNek6(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEK6 ?? 0)
        }, 0);
    }

    get totalEffectiveAreaNekUnknown(): number {
        return this.areas.reduce((result, area) => {
            return result + (area.areaNEKUnknown ?? 0)
        }, 0);
    }

    get totalAreaCompensated(): number {
        return this.compensations.reduce((sum, compensation) => {
            return sum + (compensation.area ?? 0);
        }, 0);
    }

    get totalAreaToCompensate(): number {
        return this.totalEffectiveAreaUsed - this.totalAreaCompensated;
    }

    @label("Kompensationsbilanz [m²]")
    get compensationBalance(): number {
        return this.totalAreaCompensated - this.totalEffectiveAreaUsed;
    }

    get areas(): UsageArea[] {
        if (this.areas_ === undefined) {
            this.areas_ = this.rootStore.areaStore.usageAreas.filter(a => a.projectId === this.id);
        }

        return this.areas_;
    }

    get updatedBy(): User {
        if (this.updatedBy_ === undefined) {
            const updateEntries = this.rootStore.activityLogEntryStore.activityLogEntries[Constants.usageProjectTableName]
                .filter(e => e.rowId === this.id && e.action === "update");
            const sortedUpdateEntries: ActivityLogEntry[] = _.sortBy(updateEntries, ["timestamp"]);
            const userId = _.last<ActivityLogEntry>(sortedUpdateEntries)?.userId;
            const user = this.rootStore.userStore.users.find(u => u.id === userId);

            if (user !== undefined) {
                this.updatedBy_ = user;
            } else {
                throw new DataNotLoaded("User not loaded");
            }
        }

        return this.updatedBy_;
    }

    get updatedAtTimestamp(): number {
        if (this.updatedAtTimestamp_ === undefined) {
            const updateEntries = this.rootStore.activityLogEntryStore.activityLogEntries[Constants.usageProjectTableName]
                .filter(e => e.rowId === this.id && e.action === "update");
            const sortedUpdateEntries: ActivityLogEntry[] = _.sortBy(updateEntries, ["timestamp"]);
            const timestamp = _.last<ActivityLogEntry>(sortedUpdateEntries)?.timestamp;

            if (timestamp !== undefined) {
                this.updatedAtTimestamp_ = timestamp;
            } else {
                throw new Error();
            }
        }

        return this.updatedAtTimestamp_!;
    }

    @label("Letzte Änderung")
    get updatedAt(): string {
        return moment.unix(this.updatedAtTimestamp).format(Constants.timestampFormat);
    }

    get compensations(): Compensation[] {
        if (this.compensations_ === undefined) {
            this.compensations_ = this.rootStore.compensationStore.compensations.filter(c => c.usageProjectId === this.id);
        }

        return this.compensations_;
    }

    @label("Quelle Geometrie")
    @desc("Name des Planes oder Dokuments, aus welchem die aktuelle Geometrie im Informationssystem erstellt wurde. Ist bei Plananpassungen durch die KS FFF zu aktualisieren.")
    get geometrySource(): Optional<string> {
        return this.projectDto.geometrySource ?? undefined;
    }

    get geometrySourceDate(): Nullable<Date> {
        return this.projectDto.geometrySourceDate !== null
            ? moment(this.projectDto.geometrySourceDate).toDate()
            : null;
    }

    @label("Datum Quelle Geometrie")
    @desc("Datum des Planes oder Dokuments, aus welchem die aktuelle Geometrie im Informationssystem erstellt wurde. Ist bei Plananpassungen durch die KS FFF zu aktualisieren.")
    get geometrySourceDateDisplayText(): string {
        return this.projectDto.geometrySourceDate !== null
            ? moment(this.projectDto.geometrySourceDate).format(Constants.dateFormat)
            : "";
    }
}

export class ConstructionPermitProject extends UsageProject implements ICmiDocumentationRawi {
    constructor(usageProjectDto: UsageProjectDto, rootStore: RootStoreSession) {
        super(usageProjectDto, rootStore);
        makeObservable(this, {
            usageCategory: computed,
            cmiNumberRawi: computed,
            targetArea: computed,
        });
    }

    @label("Kategorie")
    @desc("Kategorie der Hauptbeanspruchung bei Bauprojekten. Bei mehreren Beanspruchungskategorien pro Baugesuch, soll das Projekt nicht aufgespalten werden.")
    get usageCategory(): string {
        return this.rootStore.keyValueStore.getUsageCategoryDisplayText(this.projectDto.usageCategory ?? undefined);
    }

    @label("CMI-Nummer rawi")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle rawi. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiNumberRawi(): Optional<string> {
        return this.projectDto.cmiDocuments.find(d => d.department === Constants.departmentRawi && d.type === null)?.number;
    }

    @label("Kompensationsziel [m²]")
    @desc("Insgesamt zu kompensierende Fruchtfolgefläche. Dieser Wert entspricht im Normalfall dem Total der beanspruchten FFF. Es kann jedoch auch ein höherer Wert gefordert werden.")
    get targetArea(): Optional<number> {
        return this.projectDto.targetArea ?? undefined;
    }

    get issues(): string[] {
        const issues = super.issues.slice();

        const compensationGoalNotReached = this.totalAreaCompensated < (this.targetArea ?? 0);

        if (compensationGoalNotReached) {
            issues.push("Kompensationsziel nicht erreicht");
        }

        return issues;
    }
}

export class LandUsePlanChangeProject extends UsageProject implements ICmiDocumentationRawiUwe {
    constructor(usageProjectDto: UsageProjectDto, rootStore: RootStoreSession) {
        super(usageProjectDto, rootStore);
        makeObservable(this, {
            revision: computed,
            cmiDocumentsRawiPre: computed,
            cmiDocumentsRawiApproved: computed,
            cmiDocumentsUwePre: computed,
            cmiDocumentsUweApproved: computed,
            targetArea: computed,
        });
    }

    @label("Revisionsart")
    @desc("Nutzungsplanänderungen werden zwischen Gesamtrevision und Teilrevision unterschieden.")
    get revision(): string {
        return this.rootStore.keyValueStore.getRevisionTypeDisplayText(this.projectDto.revision ?? undefined);
    }

    @label("CMI-Nummer rawi (Vorprüfung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle rawi. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsRawiPre(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentRawi && d.type === "vorprüfung").map(d => d.number);
    }

    @label("CMI-Nummer rawi (Genehmigung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle rawi. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsRawiApproved(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentRawi && d.type === "genehmigung").map(d => d.number);
    }

    @label("CMI-Nummer uwe (Vorprüfung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle uwe. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsUwePre(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentUwe && d.type === "vorprüfung").map(d => d.number);
    }

    @label("CMI-Nummer uwe (Genehmigung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle uwe. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsUweApproved(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentUwe && d.type === "genehmigung").map(d => d.number);
    }

    @label("Kompensationsziel [m²]")
    @desc("Insgesamt zu kompensierende Fruchtfolgefläche. Dieser Wert entspricht im Normalfall dem Total der beanspruchten FFF. Es kann jedoch auch ein höherer Wert gefordert werden.")
    get targetArea(): Optional<number> {
        return this.projectDto.targetArea ?? undefined;
    }

    get issues(): string[] {
        const issues = super.issues.slice();

        const compensationGoalNotReached = this.totalAreaCompensated < (this.targetArea ?? 0);

        if (compensationGoalNotReached) {
            issues.push("Kompensationsziel nicht erreicht");
        }

        return issues;
    }
}

export class LandfillSiteProject extends UsageProject implements ICmiDocumentationRawiUwe {
    private recultivationProject_?: RecultivationProject;

    constructor(usageProjectDto: UsageProjectDto, rootStore: RootStoreSession) {
        super(usageProjectDto, rootStore);
        makeObservable(this, {
            landfillSiteType: computed,
            cmiDocumentsRawiPre: computed,
            cmiDocumentsRawiApproved: computed,
            cmiDocumentsUwePre: computed,
            cmiDocumentsUweApproved: computed,
            constructionPermitDate: computed,
            constructionPermitDateDisplayText: computed,
            recultivationProject: computed,
        });
    }

    @label("Projekttyp")
    @desc("Typ des Abbau-/Deponieprojekts. Bei kombinierten Abbau- und Deponieprojekten ist der Wert Rohstoffabbaustelle auszuwählen.")
    get landfillSiteType(): Optional<string> {
        return this.rootStore.keyValueStore.getLandfillSiteTypeDisplayText(this.projectDto.landfillSiteType ?? undefined)
    }

    @label("CMI-Nummer rawi (Vorprüfung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle rawi. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsRawiPre(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentRawi && d.type === "vorprüfung").map(d => d.number);
    }

    @label("CMI-Nummer rawi (Genehmigung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle rawi. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsRawiApproved(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentRawi && d.type === "genehmigung").map(d => d.number);
    }

    @label("CMI-Nummer uwe (Vorprüfung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle uwe. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsUwePre(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentUwe && d.type === "vorprüfung").map(d => d.number);
    }

    @label("CMI-Nummer uwe (Genehmigung)")
    @desc("Projektnummer im CMI Ablagesystem der Dienststelle uwe. Nur ein Geschäft erfassen, nicht mehrere.")
    get cmiDocumentsUweApproved(): string[] {
        return this.projectDto.cmiDocuments.filter(d => d.department === Constants.departmentUwe && d.type === "genehmigung").map(d => d.number);
    }

    get constructionPermitDate(): Nullable<Date> {
        return this.projectDto.constructionPermitDate !== null
            ? moment(this.projectDto.constructionPermitDate).toDate()
            : null;
    }

    @label("Datum Baubewilligung")
    @desc("Datum der letzten Baubewilligung.")
    get constructionPermitDateDisplayText(): string {
        return this.projectDto.constructionPermitDate !== null
            ? moment(this.projectDto.constructionPermitDate).format(Constants.dateFormat)
            : "";
    }

    get recultivationProject(): RecultivationProject {
        if (this.recultivationProject_ === undefined) {
            if (this.compensations.length === 1) {
                this.recultivationProject_ = this.compensations[0].creationProject as RecultivationProject;
            } else {
                throw new Error("Unexpected error occurred");
            }
        }

        return this.recultivationProject_;
    }

    get totalAreaCompensated(): number {
        return this.recultivationProject.totalEffectiveAreaCreated;
    }

    get issues(): string[] {
        const issues = super.issues.slice();

        const recultivationGoalNotReached = this.totalAreaCompensated < (this.recultivationProject.approvedCreationArea ?? 0);

        if (recultivationGoalNotReached) {
            issues.push("Rekultivierungsziel nicht erreicht");
        }

        return issues.filter(i => i !== "Handlungsbedarf bei Neuschaffungsprojekt").concat(this.recultivationProject.issues);
    }
}